import { Box, Column, Row, Switch, Tooltip, Typography } from '@tempmee/react';
import { type Control, useController } from 'react-hook-form';
import { SolidInformationCircleIcon } from '@tempmee/react-icons';
import {
  fireShiftRequestAutoconfirmFavoriteSelected,
  fireShiftRequestAutoconfirmEligibleSelected,
  fireEditShiftAutoconfirmFavoriteSelected,
  fireEditShiftAutoconfirmEligibleSelected,
  fireOpenShiftAutoconfirmFavoriteSelected,
  fireOpenShiftAutoconfirmEligibleSelected,
} from '~/posthogs/events';
import { colors } from '@tempmee/tailwind';
import { type IShiftDetailsFormValues } from '../ShiftDetails/ShiftDetailsForm/useShiftDetailsForm';

export type AutoConfirmEventContext = 'shift_request' | 'edit_shift' | 'open_shift';

/**
 * Whether the Auto-Confirm toggles are offered for a given profession/specialty. Auto-Confirm
 * is unavailable for Associate Dentist / Front Desk and for any non-General specialty. Shared
 * so callers can gate experiment defaulting on the same rule the field renders by.
 */
export const isAutoConfirmAvailable = (professionType: string, specialty: string): boolean => {
  if (['Associate Dentist', 'Front Desk'].includes(professionType)) return false;
  return Boolean(specialty?.toLowerCase()?.includes('general'));
};

interface IAutoConfirmFieldProps {
  control: Control<IShiftDetailsFormValues>;
  professionType: string;
  preferredSpecialty: string;
  eventContext?: AutoConfirmEventContext;
  hideHeader?: boolean;
  disabled?: boolean;
  /** Renders the Favorites toggle inactive (used when the office has no favorites). */
  favoritesDisabled?: boolean;
}

const TOOLTIP_CONTENT = (
  <Typography as="span" bold color="#FFFFFF">
    Professional&apos;s eligibility criteria:
    <br />
    <br />
    {'\u2022'} Has at least a 95% attendance score for previous shifts worked.
    <br />
    {'\u2022'} Has a minimum of 2 years working experience.
    <br />
    {'\u2022'} Has received exceptional performance ratings on over 95% of completed shifts.
    <br />
    {'\u2022'} Has completed at least 3 shifts.
  </Typography>
);

export const AutoConfirmField = ({
  control,
  professionType,
  preferredSpecialty,
  eventContext,
  hideHeader = false,
  disabled = false,
  favoritesDisabled = false,
}: IAutoConfirmFieldProps) => {
  const { field } = useController({ name: 'autoConfirm', control });

  const getAutoFillInfo = (professionType: string, specialty: string) => {
    if (['Associate Dentist', 'Front Desk'].includes(professionType)) {
      return {
        show: false,
        message: `The Auto-Confirm feature is not available for ${professionType} at this time.`,
      };
    }

    if (!isAutoConfirmAvailable(professionType, specialty)) {
      return {
        show: false,
        message:
          'The Auto-Confirm feature is only available for shifts without a specialty preference (General).',
      };
    }

    return {
      show: true,
      message: (
        <Box>
          - Auto-Confirm does not apply to counter offers.
          <br />- Selecting “Favorites” will send them invites.
        </Box>
      ),
    };
  };

  const autoFillInfo = getAutoFillInfo(professionType, preferredSpecialty);

  if (!autoFillInfo?.show) {
    return (
      <Column
        data-testid="auto-confirm-field"
        data-cy="auto-confirm-field"
        style={{ gap: 12 }}
      >
        {!hideHeader && (
          <Row align="center" gap={2}>
            <Typography variant="subtitle1" color={colors.neutral[600]}>
              Auto-Confirm:
            </Typography>
          </Row>
        )}
        <Typography variant="body1"
          italic
          color={colors.neutral[500]}
          data-testid="auto-confirm-unavailable-message"
          data-cy="auto-confirm-unavailable-message"
          style={{ padding: '0 24px' }}
        >
          The Auto-Confirm feature is only available for shifts without a specialty preference
          (General).
        </Typography>
      </Column>
    );
  }

  const fireToggleEvent = (key: 'favorites' | 'eligibleProfessionals', newValue: boolean) => {
    if (!eventContext) return;

    const payload = {
      autoConfirm: (newValue ? 'on' : 'off') as 'on' | 'off',
      designDate: 202603,
    };

    const eventMap = {
      shift_request: {
        favorites: fireShiftRequestAutoconfirmFavoriteSelected,
        eligibleProfessionals: fireShiftRequestAutoconfirmEligibleSelected,
      },
      edit_shift: {
        favorites: fireEditShiftAutoconfirmFavoriteSelected,
        eligibleProfessionals: fireEditShiftAutoconfirmEligibleSelected,
      },
      open_shift: {
        favorites: fireOpenShiftAutoconfirmFavoriteSelected,
        eligibleProfessionals: fireOpenShiftAutoconfirmEligibleSelected,
      },
    };

    eventMap[eventContext][key](payload);
  };

  const handleToggle = (key: 'favorites' | 'eligibleProfessionals') => {
    if (disabled) return;
    if (key === 'favorites' && favoritesDisabled) return;

    const current = field.value ?? { favorites: false, eligibleProfessionals: false };
    const newValue = !current[key];
    field.onChange({
      ...current,
      [key]: newValue,
    });
    fireToggleEvent(key, newValue);
  };

  return (
    <Column
      data-testid="auto-confirm-field"
      data-cy="auto-confirm-field"
      style={{ padding: '0 24px', gap: 12 }}
    >
      {!hideHeader && (
        <Row align="center" gap={2} style={{ marginBottom: 8 }}>
          <Typography variant="subtitle1" color={colors.neutral[600]}>
            Auto-Confirm:
          </Typography>
        </Row>
      )}

      <Column
        data-testid="auto-confirm-toggles"
        data-cy="auto-confirm-toggles"
        aria-disabled={disabled}
        style={{ gap: 12 }}
      >
        <Switch
          checked={field.value?.favorites ?? false}
          onChange={() => handleToggle('favorites')}
          disabled={disabled || favoritesDisabled}
          color="favorite"
          label={
            <Typography variant="body1" color={colors.neutral[600]}>
              Favorites
            </Typography>
          }
          data-testid="favorites-toggle"
          data-cy="favorites-toggle"
        />

        <Row align="center" gap={2}>
          <Switch
            checked={field.value?.eligibleProfessionals ?? false}
            onChange={() => handleToggle('eligibleProfessionals')}
            disabled={disabled}
            label={
              <Typography variant="body1" color={colors.neutral[600]}>
                Eligible Professionals
              </Typography>
            }
            data-testid="eligible-toggle"
            data-cy="eligible-toggle"
          />
          <Tooltip placement="bottom" title={TOOLTIP_CONTENT}>
            <SolidInformationCircleIcon color={colors.neutral[300]} />
          </Tooltip>
        </Row>
      </Column>

      <Box
        data-testid="auto-confirm-message"
        data-cy="auto-confirm-message"
        style={{ marginTop: 8 }}
      >
        <Typography variant="body1" color={colors.neutral[500]}>
          {autoFillInfo.message}
        </Typography>
      </Box>
    </Column>
  );
};
